import React from 'react'
import { reactNodeIsOfType } from 'component_library/utils'

/**
 * Props for the If component to conditionally render children.
 */
type IfProps = {
  /** Condition to evaluate for rendering children */
  condition: boolean
  /** Children elements or a function returning elements */
  children: React.ReactNode | (() => React.ReactNode)
}

/**
 * Props for the Choose component that renders When or Otherwise components.
 */
type ChooseProps = {
  /** Children should include When or Otherwise components */
  children:
    | React.ReactElement<WhenProps | OtherwiseProps>
    | React.ReactElement<WhenProps | OtherwiseProps>[]
}

/**
 * Props for the When component to render children based on a condition.
 */
type WhenProps = {
  /** Condition to evaluate for rendering children */
  condition: boolean
  /** Children elements or a function returning elements */
  children: React.ReactNode | (() => React.ReactNode)
}

/**
 * Props for the Otherwise component to provide a fallback when no When condition is true.
 */
type OtherwiseProps = {
  /** Children elements or a function returning elements */
  children: React.ReactNode | (() => React.ReactNode)
}

/**
 * Renders children if the condition is true.
 * Supports lazy evaluation with a function-based children prop.
 */
export const If: React.FC<IfProps> = ({ condition, children }) => {
  if (!condition) {
    return null
  }

  if (typeof children === 'function') {
    return <>{children()}</>
  }

  return <>{children}</>
}

/**
 * Evaluates When and Otherwise components to render the first matching When or the Otherwise fallback.
 */
export const Choose: React.FC<ChooseProps> = ({ children }) => {
  const validChild = React.Children.toArray(children).find(
    (child) =>
      React.isValidElement(child) &&
      reactNodeIsOfType(child, When) &&
      child.props.condition
  )

  if (validChild && React.isValidElement(validChild)) {
    if (typeof validChild.props.children === 'function') {
      return <>{validChild.props.children()}</>
    }

    return <>{validChild}</>
  }

  const otherwiseChild = React.Children.toArray(children).find(
    (child) =>
      React.isValidElement(child) && reactNodeIsOfType(child, Otherwise)
  )

  if (otherwiseChild && React.isValidElement(otherwiseChild)) {
    if (typeof otherwiseChild.props.children === 'function') {
      return <>{otherwiseChild.props.children()}</>
    }

    return <>{otherwiseChild}</>
  }

  return null
}

/**
 * Renders children if the condition is true.
 * Supports lazy evaluation with a function-based children prop.
 */
export const When: React.FC<WhenProps> = ({ condition, children }) => {
  if (!condition) {
    return null
  }

  if (typeof children === 'function') {
    return <>{children()}</>
  }

  return <>{children}</>
}

/**
 * Provides a fallback rendering when no When component condition is satisfied.
 * Supports lazy evaluation with a function-based children prop.
 */
export const Otherwise: React.FC<OtherwiseProps> = ({ children }) => {
  if (typeof children === 'function') {
    return <>{children()}</>
  }

  return <>{children}</>
}
